import { Component, HostListener, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { EapService } from '@service/eap.service';
import { EapLedPattern } from 'icreate/common/models/eap';
import { lastValueFrom, take } from 'rxjs';
import { NewPatternSettingDialog } from '../new-pattern-setting-dialog/new-pattern-setting-dialog.component';
import { MoreOption, MoreOptionAction } from '@components/more-options/more-options-dropdown.component';

@Component({
    selector: 'app-led-pattern-setting-dialog',
    templateUrl: './led-pattern-setting-dialog.component.html',
    styleUrl: './led-pattern-setting-dialog.component.css',
    standalone: false,
})
export class LEDPatternSettingDialog {
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    @ViewChild('deleteConfirmation') deleteConfirmation: ConfirmationWindowComponent;

    ledPatternList: EapLedPattern[] = [];

    selectedIndex: number;
    isOpenOption: boolean = false;
    deletePatternTitle: string;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ledPatternList: EapLedPattern[];
            vesselId;
            userCanEdit;
        },
        private dialogRef: MatDialogRef<LEDPatternSettingDialog>,
        private eapService: EapService,
        private dialog: MatDialog
    ) {}

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeMoreOption();
    }

    ngOnInit(): void {
        this.ledPatternList = this.data.ledPatternList ?? [];
    }

    async addPattern(): Promise<void> {
        const dialogRef = this.dialog.open(NewPatternSettingDialog, {
            disableClose: true,
            width: '700px',
            height: '750px',
            data: {
                ledPattern: null,
                vesselId: this.data.vesselId,
            },
        });

        await lastValueFrom(dialogRef.afterClosed());
    }

    async editPattern(pattern: EapLedPattern): Promise<void> {
        this.closeMoreOption();

        const dialogRef = this.dialog.open(NewPatternSettingDialog, {
            disableClose: true,
            width: '700px',
            height: '750px',
            data: {
                ledPattern: pattern,
                vesselId: this.data.vesselId,
            },
        });

        const updatedPattern = await lastValueFrom(dialogRef.afterClosed());
        if (updatedPattern) {
            const index = this.ledPatternList.findIndex(item => item.id === updatedPattern.id);

            if (index !== -1) {
                this.ledPatternList[index] = updatedPattern;
            }
        }
    }

    get patternOptions(): SimpleDropdownOption[] {
        return this.ledPatternList.map(pattern => ({
            value: pattern.id,
            string: pattern.name,
        }));
    }

    close() {
        this.dialogRef.close();
    }

    openMoreOption(index: number): void {
        if (this.selectedIndex === index && this.isOpenOption) {
            this.closeMoreOption();
            return;
        }
        this.selectedIndex = index;
        this.isOpenOption = true;
    }

    closeMoreOption(): void {
        this.isOpenOption = false;
        this.selectedIndex = null;
    }

    async deletePattern(pattern: EapLedPattern): Promise<void> {
        this.deletePatternTitle = pattern.name;
        this.deleteConfirmation.setVisible(true);
        try {
            const confirmationOption = await lastValueFrom(this.deleteConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.eapService.deleteLedPattern(pattern);
            }
        } catch (err) {
            console.error('Failed to delete LED pattern:', err);
        } finally {
            this.deletePatternTitle = '';
        }
    }

    get moreOptions(): MoreOption[] {
        return [
            {
                action: MoreOptionAction.EDIT,
                string: 'dlg.edit',
                disabled: !this.data.userCanEdit,
            },
            {
                action: MoreOptionAction.DELETE,
                string: 'dlg.delete',
                disabled: !this.data.userCanEdit,
            },
        ];
    }

    onMoreOptionSelected(pattern: EapLedPattern, action: MoreOptionAction): void {
        switch (action) {
            case MoreOptionAction.EDIT:
                this.editPattern(pattern);
                break;

            case MoreOptionAction.DELETE:
                this.deletePattern(pattern);
                break;
        }
    }
}
